import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Message } from "@bbwt/classes";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { FilterComponent, FilterInputType, FilterType, IFilterSettings, StringFilterMatchMode } from "@features/filter";
import { ITableSettings, IGridColumn, IGridSettings, GridComponent, UpdateMode, CreateMode } from "@features/grid";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { Permissions } from "@main/permissions/permissions";
import { CoreRole } from "@main/roles";
import { UserHelperService } from "@main/users/user-helper.service";
import { OrganizationGroup } from "@mipro/custom-controls/organization-group";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { SquadType } from "@project/models/squad";
import { ConfirmationService, MessageService, SelectItem, SelectItemGroup } from "primeng/api";
import { Subscription } from "rxjs";
import { EditPatientInfo, PatientPageItem } from "./patient-page-item";
import { PlayerService } from "./player.service";
import { HistoricalItem } from "@mipro/classes/historical-item";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { PlayerOfflineStorage } from "./player-offline.storage";
import { WalkInOfflineStorage } from "@project/walk-ins/classes/walk-in-offline.storage";
import { EpisodeOfflineStorage } from "@project/episode/episode-offline.storage";
import { PrescriptionOfflineStorage } from "@project/prescription/classes/prescription-offline.storage";
import { FormEventType } from "@main/formio/components/form-events/event-type";
import { FormEventPayload } from "@main/formio/dto/formEventsDTO";
import { FormEventsService } from "@main/formio/services/formEvents.service";
import { PatientLogin } from "@main/users";

@Component({
    selector: "players-grid",
    templateUrl: "./players-grid.component.html",
    styleUrls: ["./players-grid.component.scss"]
})
export class PlayersGridComponent implements OnInit, OnDestroy {
    private squads: { [id: string]: { squadName: string; orgName: string } } = {};
    private currentUserAccessibleSquads: { [id: string]: { squadName: string; orgName: string } } = {};
    private reloadClaimsSubscription: Subscription;
    private refreshRowButtonsSubscription: Subscription;
    private refreshOrganizationGroupsSubscription: Subscription;

    FormEventType = FormEventType;

    patientShowHistorical = false;
    patientHistoricalItems: HistoricalItem[] = [];
    filterVisible: boolean = false;

    @ViewChild("grid") patientsGrid: GridComponent;
    @ViewChild("editInfoGrid") editInfoGrid: GridComponent;

    @ViewChild(FilterComponent) filterComponent: FilterComponent;

    initializingGrid = true;
    editingPatient = false;
    tableSettings: ITableSettings = {
        columns: <IGridColumn[]>[
            { field: "firstName", header: "First Name" },
            { field: "lastName", header: "Last Name" },
            {
                field: "organizationGroups",
                header: this.namingConvService.groupSingular,
                displayHandler: (squadIds: string[]) =>
                    squadIds
                        ?.filter(id => this.currentUserAccessibleSquads[id])
                        ?.map(id => {
                            if (this.currentUserAccessibleSquads[id]) {
                                return `${this.currentUserAccessibleSquads[id].squadName} (${this.currentUserAccessibleSquads[id].orgName})`;
                            }
                        })
                        .join(", "),
                sortable: false
            }
        ],
        sortField: "firstName"
    };
    gridSettings: IGridSettings = {
        createMode: CreateMode.Disabled,
        updateMode: UpdateMode.Disabled,
        deletingEnabled: false,
        dataService: this.playerService,
        rowStyleClassFunc: (rowData: PatientPageItem) => ({
            "offline-patient": rowData.savedOffline,
            "self-register-patient": rowData.selfRegister
        }),
        loadedDataHandler: data => {
            const offlinePatients = PlayerOfflineStorage.getAll().filter(x => !x.selfRegister);
            data.items = offlinePatients.concat(data.items);
            data.total = offlinePatients.length + data.total;
            return data;
        },
        additionalRowActions: [
            {
                primeIcon: "pi pi-save",
                buttonClass: "p-button-secondary p-button-rounded",
                label: "Save Online",
                allowOffline: () => true,
                visible: (rowData: PatientPageItem) =>
                    this.appOnlineStateService.isAppOnline &&
                    rowData.savedOffline &&
                    this.userHelperService.hasPermission(Permissions.WritePatient) &&
                    !rowData.currentOrganizationArchived,
                handler: (rowData: PatientPageItem) => {
                    this.offlinePatient = PlayerOfflineStorage.get(rowData.id);
                    this.editingPatient = true;
                }
            },
            {
                primeIcon: "pi pi-user",
                buttonClass: "p-button-rounded p-button-secondary p-button-text",
                visible: (rowData: PatientPageItem) => !rowData.currentOrganizationArchived,
                handler: (rowData: PatientPageItem) => {
                    this.patientService.currentPatientPlaceHolder = {
                        patientId: rowData.patientId,
                        fullName: rowData.firstName + " " + rowData.lastName
                    };
                }
            },
            {
                primeIcon: "mi mi-button-edit",
                buttonClass: "p-button-rounded p-button-secondary p-button-text",
                handler: async (rowData: PatientPageItem) => {
                    if (!rowData.savedOffline) {
                        this.editPatient(rowData);
                    } else {
                        this.offlinePatient = PlayerOfflineStorage.get(rowData.id);
                        this.editingPatient = true;
                    }
                },
                allowOffline: () => true,
                visible: (rowData: PatientPageItem) => {
                    if (this.userHelperService.hasPermission(Permissions.WritePatient) && !rowData.currentOrganizationArchived) {
                        if (this.appOnlineStateService.isAppOnline) {
                            return !rowData.savedOffline;
                        }
                        return rowData.savedOffline;
                    }
                    return false;
                }
            },
            {
                primeIcon: "mi mi-button-remove",
                buttonClass: "p-button-rounded p-button-text p-button-danger",
                handler: (rowData: PatientPageItem) => this.deletePatient(rowData),
                allowOffline: () => true,
                visible: (rowData: PatientPageItem) =>
                    this.actionVisible(rowData) &&
                    this.userHelperService.hasPermission(Permissions.ArchivePatient) &&
                    !this.isOfflinePatientUsed(rowData.id) &&
                    !rowData.currentOrganizationArchived
            },
            {
                primeIcon: "pi pi-history",
                buttonClass: "p-button-rounded p-button-secondary p-button-text",
                allowOffline: () => true,
                visible: (rowData: PatientPageItem) => !rowData.currentOrganizationArchived,
                handler: async (rowData: PatientPageItem) => {
                    this.patientHistoricalItems = await this.playerService.getHistoricalItems(rowData.patientId);
                    this.patientShowHistorical = true;
                }
            },
            {
                primeIcon: "pi pi-undo",
                buttonClass: "p-button-rounded p-button-secondary p-button-text",
                visible: (rowData: PatientPageItem) => rowData.currentOrganizationArchived,
                handler: async (rowData: PatientPageItem) => {
                    await this.restorePatientOrganisation(rowData.patientId);
                }
            }
        ]
    };

    isOnline = this.appOnlineStateService.isAppOnline;

    filterSettings: IFilterSettings[] = [
        {
            header: "",
            valueFieldName: "groups",
            inputType: FilterInputType.Multiselect,
            autoSubmitSelectableInputChange: true
        },
        {
            header: "First name",
            valueFieldName: "firstName",
            matchModeSelectorVisible: false,
            matchMode: StringFilterMatchMode.Contains,
            disable: () => !this.isOnline
        },
        {
            header: "Last name",
            valueFieldName: "lastName",
            matchModeSelectorVisible: false,
            matchMode: StringFilterMatchMode.Contains,
            disable: () => !this.isOnline
        },
        {
            header: "Show Archived",
            valueFieldName: "showArchived",
            matchModeSelectorVisible: false,
            filterType: FilterType.Boolean
        }
    ];

    multipleOrganizationFilter = false;
    groupOptions: SelectItemGroup[] | SelectItem[] = [];
    selectEditInfoVisible = false;
    editInfoGridSettings: IGridSettings = {
        updateFunc: (editInfo: EditPatientInfo) => this.editPatientInfo([editInfo]),
        deleteFunc: (editInfo: EditPatientInfo) => this.deletePatientInfo([editInfo]),
        createMode: CreateMode.Disabled,
        deletingAvailable: () => this.editInfoAction === "Delete",
        updateAvailable: () => this.editInfoAction === "Update"
    };
    editInfoTableSettings: ITableSettings = {
        lazy: false,
        columns: <IGridColumn[]>[
            { field: "firstName", header: "First Name" },
            { field: "lastName", header: "Last Name" },
            { field: "email", header: "Email" }
        ],
        value: []
    };
    editInfoAction: "Update" | "Delete" = "Update";

    offlinePatient: PatientLogin;
    appOnlineStateSubscription: Subscription;

    constructor(
        private playerService: PlayerService,
        private router: Router,
        public namingConvService: NamingConventionService,
        private patientService: PatientService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private broadcastService: BroadcastService,
        private userHelperService: UserHelperService,
        public appOnlineStateService: AppOnlineStateService,
        private formEventsService: FormEventsService
    ) {
        this.reloadClaimsSubscription = broadcastService
            .on(UserHelperService.ClaimsUpdatedEventName)
            .subscribe(() => {
                if (this.patientsGrid) {
                    this.patientsGrid.setTableProperty("first", 0);
                    this.patientsGrid.reload();
                }
            });
    }

    async ngOnInit() {
        this.appOnlineStateSubscription = this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe({
                next: data => {
                    this.isOnline = data.isAppOnline;
                }
            });

        this.multipleOrganizationFilter = this.userHelperService.hasRole(CoreRole.MiProAdmin, CoreRole.SuperAdmin);
        await this.initOrganizationGroups();

        this.refreshRowButtonsSubscription = this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe(_ => this.patientsGrid.reload());

        this.refreshOrganizationGroupsSubscription = this.broadcastService
            .on(UserHelperService.ClaimsUpdatedEventName)
            .subscribe(() => this.initOrganizationGroups());

        this.initializingGrid = false;
    }

    private async initOrganizationGroups() {
        const orgGroups = await this.playerService.getOrganizationGroups(
            this.multipleOrganizationFilter ? null : this.userHelperService.hashedOrganizationId
        );
        const currentUserOrganizationGroups = await this.playerService.getOrganizationGroups();
        this.currentUserAccessibleSquads = this.buildSquadsArray(currentUserOrganizationGroups);

        this.setupGroupOptions(orgGroups);
    }

    ngOnDestroy() {
        this.reloadClaimsSubscription?.unsubscribe();
        this.refreshRowButtonsSubscription?.unsubscribe();
        this.refreshOrganizationGroupsSubscription?.unsubscribe();
        this.appOnlineStateSubscription?.unsubscribe();
    }

    async patientCreated(event: PatientLogin) {
        await this.patientsGrid.reload();
        this.patientService.currentPatientId = event.patientId;

        if (event?.patientId) {
            this.formEventsService.triggerFormEvent(<FormEventPayload> {
                formEventType: FormEventType.CreatePatient,
                episodeId: null,
                patientId: event.patientId
            });
        }

    }

    private reorderOrganizations(orgGroups: OrganizationGroup[]) {
        return orgGroups
            .filter(org => org.id === this.userHelperService.hashedOrganizationId)
            .concat(orgGroups.filter(org => org.id !== this.userHelperService.hashedOrganizationId));
    }

    private setupGroupOptions(orgGroups: OrganizationGroup[]) {
        const orderedOrgs = this.reorderOrganizations(orgGroups);
        this.groupOptions = this.multipleOrganizationFilter
            ? orderedOrgs
                  .map(og => ({
                      label: og.name,
                      value: og.id,
                      items: this.buildOrganizationOptions(og)
                  }))
                  .filter(g => g.items?.length > 0)
            : this.buildOrganizationOptions(orderedOrgs.find(og => og.id === this.userHelperService.hashedOrganizationId));
    }

    private buildOrganizationOptions(og: OrganizationGroup): SelectItem[] {
        if (og) {
            return og.groups
                .filter(g => g.type !== SquadType.UnassignedPatients && !g.archived)
                .map(g => {
                    this.squads[g.id] = { squadName: g.name, orgName: og.name };
                    return { label: g.name, value: g.id };
                });
        } else return [];
    }

    private buildSquadsArray(orgGroups: OrganizationGroup[]): { [id: string]: { squadName: string; orgName: string } } {
        if (orgGroups.length) {
            const result = {};
            orgGroups.forEach(og =>
                og.groups
                    .filter(g => g.type !== SquadType.UnassignedPatients && !g.archived)
                    .forEach(g => (result[g.id] = { squadName: g.name, orgName: og.name }))
            );
            return result;
        } else return {};
    }

    autoSearchOn() {
        if (this.filterComponent) {
            this.filterComponent.search();
        }
    }

    private editPatient(rowData: PatientPageItem) {
        this.editInfoAction = "Update";
        if (rowData.editInfo?.length > 0) {
            this.editPatientInfo(rowData.editInfo, rowData);
        } else {
            const queryParams: { profileId: string; offlineId: string } = {
                offlineId: rowData.savedOffline ? rowData.id : null,
                profileId: rowData.savedOffline ? null : rowData.patientId
            };
            this.router.navigate(["/app/patients/edit/0"], { queryParams: queryParams }).then();
        }
    }

    private editPatientInfo(editInfo: EditPatientInfo[], patientInfo?: { firstName: string; lastName: string }) {
        if (editInfo.length > 1) {
            this.openEditInfoDialog(editInfo, patientInfo);
        } else {
            this.router.navigate(["/app/patients/edit", editInfo[0].id]).then();
        }
    }

    private deletePatient(rowData: PatientPageItem) {
        this.editInfoAction = "Delete";
        if (rowData.savedOffline) {
            this.deleteWithConfirmation(async () => {
                await this.playerService.delete(rowData.id);
                this.patientService.triggerPatientsUpdated();
            });
        } else if (rowData.editInfo?.length > 0) {
            this.deletePatientInfo(rowData.editInfo, rowData);
        } else {
            this.deleteWithConfirmation(() =>
                this.playerService.archivePatient(rowData.patientId).then(() => this.patientService.triggerPatientsUpdated())
            );
        }
    }

    private deletePatientInfo(editInfo: EditPatientInfo[], patientInfo?: { firstName: string; lastName: string }) {
        if (editInfo.length > 1) {
            this.openEditInfoDialog(editInfo, patientInfo);
        } else {
            this.deleteWithConfirmation(() =>
                this.playerService
                    .delete(editInfo[0].id, { showSuccessMessage: false })
                    .then(() => this.patientService.triggerPatientsUpdated())
            );
        }
    }

    private deleteWithConfirmation(onAccept: () => Promise<any>) {
        this.confirmationService.confirm({
            message: "Are you sure you want to remove this patient?",
            accept: async () => {
                await onAccept();
                this.messageService.add(Message.Success("Patient has been removed", "Patient"));
                this.patientsGrid.reload().then();
            }
        });
    }

    private openEditInfoDialog(editInfo: EditPatientInfo[], patientInfo?: { firstName: string; lastName: string }) {
        const data = editInfo.map(i => Object.assign({}, patientInfo ?? {}, i));
        this.editInfoGrid.setTableProperty("value", data);
        this.selectEditInfoVisible = true;
    }

    private actionVisible(rowData: PatientPageItem) {
        return this.appOnlineStateService.isAppOnline || rowData.savedOffline;
    }

    private isOfflinePatientUsed(id: string) {
        const hasRelatedWalkIns = WalkInOfflineStorage.getAll().some(w => w.patientId == id);
        const hasRelatedEpisodes = EpisodeOfflineStorage.getAll().some(w => w.patientUserId == id);
        const hasRelatedPrescriptions = PrescriptionOfflineStorage.getAll().some(w => w.patientId == id);

        return hasRelatedWalkIns || hasRelatedEpisodes || hasRelatedPrescriptions;
    }

    onAddPatientClick() {
        this.offlinePatient = null;
        this.editingPatient = true;
    }

    private async restorePatientOrganisation(patientId: string): Promise<void> {
        await this.playerService.restorePatientOrganisation(patientId);
        await this.patientsGrid.reload();
        this.patientService.triggerPatientsUpdated();
    }

    toggleFilter() {
        this.filterVisible = !this.filterVisible;
    }
}
